import React from 'react';
import { useParams } from 'react-router-dom';
import { useQueries, useQueryClient } from 'react-query';
import * as Cesium from 'cesium';
import { Clock } from 'resium';
import API from '@wheeebo/polaris-api';
import { clearMonitorMapHandoff, readMonitorMapHandoff, useMap, useErrorHandler, useMonitorMap, isLikelyUserMapCenter, pickRadarCenter, zoomToRoughAltitudeMeters, } from '@wheeebo/polaris-ui';
import CesiumMap from './CesiumMap';
import FootprintEntity from './FootprintEntity';
import StaticFlightPathEntity from './StaticFlightPathEntity';
import WaypointEntity from './WaypointEntity';
import RadarEntity from './RadarEntity';
const INITIAL_ITEM_COUNT = 25;
const NO_FLY_ZONE_SEARCH_RADIUS_METER = 10000;
const MAP_UPDATE_INTERVAEL_MSEC = 3000;
function MonitorPanel({ env, selectedFlightPathId, infoBoxLeftPadding }) {
    const params = useParams();
    const { showAndEmitJsErrorEvent } = useErrorHandler();
    const { center, setCenter } = useMap();
    /** Read once when /monitor3d mounts (sessionStorage survives route change until cleared). */
    const handoffPayloadRef = React.useRef(readMonitorMapHandoff());
    const [handoffCameraAltitudeMeters] = React.useState(() => {
        const h = handoffPayloadRef.current;
        if (!h) {
            return undefined;
        }
        return h.altitudeMeters ?? zoomToRoughAltitudeMeters(h.zoom ?? 14, h.latitude);
    });
    React.useLayoutEffect(() => {
        const h = handoffPayloadRef.current;
        if (!setCenter) {
            clearMonitorMapHandoff();
            return;
        }
        if (h) {
            setCenter({ lat: h.latitude, lng: h.longitude });
        }
        clearMonitorMapHandoff();
    }, [setCenter]);
    const queryClient = useQueryClient();
    const { radarRadius, selectedVehicleId, setSelectedVehicleId, vehicles, setVehicles, showFootprint, showRadar, showFlightPath, showGeoFence, showSafePort, showNoFlyZone, } = useMonitorMap();
    const [previousLoadTime, setPreviousLoadTime] = React.useState();
    const [latency, setLatency] = React.useState(MAP_UPDATE_INTERVAEL_MSEC);
    const [start, setStart] = React.useState();
    const [stop, setStop] = React.useState();
    const [geoFenceToDisplay, setGeoFenceToDisplay] = React.useState();
    const [safePortToDisplay, setSafePortToDisplay] = React.useState();
    const [flightPathToDisplay, setFlightPathToDisplay] = React.useState();
    const flightPathOptions = { missionId: params.missionId, preload: ['waypoints'] };
    const safePortOptions = {};
    const noFlyZoneOptions = {
        latitude: center?.lat || 0,
        longitude: center?.lng || 0,
        radius: NO_FLY_ZONE_SEARCH_RADIUS_METER,
    };
    const monitorOptions = {
        limit: INITIAL_ITEM_COUNT,
        latitude: center?.lat || 0,
        longitude: center?.lng || 0,
        radius: radarRadius,
        missionId: params.missionId,
    };
    const vehicleOptions = {
        ids: vehicles?.map((e) => e.vehicle.id),
        missionId: params.missionId,
        preload: ['waypoints', 'safe_ports', 'geo_fences'],
    };
    const [{ data: geoFences }, { data: flightPaths }, { data: safePorts }, { data: noFlyZones }, { data: footprints }, { data: vehicleList },] = useQueries([
        {
            queryKey: params.missionId ? API.GeoFences.listKey(params.missionId, {}) : '',
            queryFn: () => (params.missionId ? API.GeoFences.list(params.missionId, {}) : undefined),
            enabled: !!params.missionId,
            keepPreviousData: true,
        },
        {
            queryKey: params.missionId ? API.FlightPaths.listKey(flightPathOptions) : '',
            queryFn: () => (params.missionId ? API.FlightPaths.list(flightPathOptions) : undefined),
            enabled: !!params.missionId,
            keepPreviousData: true,
        },
        {
            queryKey: params.missionId ? API.SafePorts.listKey(params.missionId, safePortOptions) : '',
            queryFn: () => (params.missionId ? API.SafePorts.list(params.missionId, safePortOptions) : undefined),
            enabled: !!params.missionId,
            keepPreviousData: true,
        },
        {
            queryKey: API.NoFlyZones.listKey(noFlyZoneOptions),
            queryFn: () => API.NoFlyZones.list(noFlyZoneOptions),
            keepPreviousData: true,
            enabled: isLikelyUserMapCenter(center),
        },
        {
            queryKey: isLikelyUserMapCenter(center) ? API.Monitor.listKey(monitorOptions) : '',
            queryFn: () => (isLikelyUserMapCenter(center) ? API.Monitor.list(monitorOptions) : undefined),
            enabled: isLikelyUserMapCenter(center) && radarRadius >= 100 && radarRadius <= 100000,
            keepPreviousData: true,
            refetchInterval: MAP_UPDATE_INTERVAEL_MSEC,
            onError: showAndEmitJsErrorEvent,
        },
        {
            queryKey: API.Vehicles.listKey(vehicleOptions),
            queryFn: () => API.Vehicles.list(vehicleOptions),
            enabled: (vehicles?.length || 0) > 0,
            keepPreviousData: true,
        },
    ]);
    React.useEffect(() => {
        setVehicles?.(footprints?.items || []);
        const oldKeys = new Set(footprints?.items?.map((e) => e.vehicle.id));
        const newKeys = new Set(vehicles?.map((e) => e.vehicle.id));
        if (oldKeys.size !== newKeys.size || ![...oldKeys].every((e) => newKeys.has(e))) {
            queryClient.invalidateQueries(API.Vehicles.CacheKey);
        }
    }, [footprints, setVehicles]);
    React.useEffect(() => {
        const map = new Map();
        flightPaths?.items?.forEach((e) => map.set(e.id, e));
        vehicleList?.items?.forEach((e) => {
            const flightPath = e.activeFlightPlan?.flightPath;
            if (flightPath) {
                map.set(flightPath.id, flightPath);
            }
        });
        setFlightPathToDisplay([...map.values()]);
    }, [flightPaths?.items, vehicleList]);
    React.useEffect(() => {
        const map = new Map();
        geoFences?.items?.forEach((e) => map.set(e.id, e));
        vehicleList?.items?.forEach((e) => e.activeFlightPlan?.flightPath?.mission?.geoFences?.forEach((e) => map.set(e.id, e)));
        setGeoFenceToDisplay([...map.values()]);
    }, [geoFences?.items, vehicleList]);
    React.useEffect(() => {
        const map = new Map();
        safePorts?.items?.forEach((e) => map.set(e.id, e));
        vehicleList?.items?.forEach((e) => e.activeFlightPlan?.flightPath?.mission?.safePorts?.forEach((e) => map.set(e.id, e)));
        setSafePortToDisplay([...map.values()]);
    }, [safePorts?.items, vehicleList]);
    React.useEffect(() => {
        if (!footprints?.items?.length) {
            setFlightPathToDisplay([]);
            setGeoFenceToDisplay([]);
            setSafePortToDisplay([]);
            return;
        }
        const now = new Date();
        const latency = previousLoadTime ? now.getTime() - previousLoadTime.getTime() : MAP_UPDATE_INTERVAEL_MSEC;
        if (latency < MAP_UPDATE_INTERVAEL_MSEC) {
            return;
        }
        const oldestTime = footprints.items
            .flatMap((e) => e.path)
            .reduce((prev, curr) => Math.min(prev, curr.time), Number.MAX_VALUE);
        setStart(Cesium.JulianDate.fromDate(new Date(oldestTime + latency)));
        setStop(Cesium.JulianDate.fromDate(new Date(now.getTime() + latency)));
        setLatency(latency);
        setPreviousLoadTime(now);
    }, [footprints, previousLoadTime]);
    const radarVehiclePaths = vehicles?.length ? vehicles : footprints?.items;
    const radarLocation = React.useMemo(() => pickRadarCenter(center, radarVehiclePaths, selectedVehicleId), [center, radarVehiclePaths, selectedVehicleId]);
    return (React.createElement(CesiumMap, { env: env, infoBoxLeftPadding: infoBoxLeftPadding, handoffCameraAltitudeMeters: handoffCameraAltitudeMeters, realtime: true, geoFences: showGeoFence ? geoFenceToDisplay : [], safePorts: showSafePort ? safePortToDisplay : [], noFlyZones: showNoFlyZone ? noFlyZones?.items : [], footprints: footprints?.items, onSelectedVehicleIdChanged: setSelectedVehicleId },
        React.createElement(React.Fragment, null,
            React.createElement(Clock, { startTime: start?.clone(), stopTime: stop?.clone(), clockStep: Cesium.ClockStep.SYSTEM_CLOCK }),
            showFlightPath
                ? flightPathToDisplay?.map((e) => (React.createElement(StaticFlightPathEntity, { key: e.id, id: e.id, tracked: e.id === selectedFlightPathId, positions: e.waypoints?.map((e) => Cesium.Cartesian3.fromDegrees(e.location.lng, e.location.lat, e.altitude)) })))
                : null,
            showFlightPath
                ? flightPathToDisplay?.flatMap((flightPath) => flightPath.waypoints?.map((waypoint) => (React.createElement(WaypointEntity, { key: waypoint.id, flightPath: flightPath, waypoint: waypoint, tracked: flightPath.id === selectedFlightPathId }))))
                : null,
            showFootprint && start && stop
                ? footprints?.items?.map((e) => (React.createElement(FootprintEntity, { key: e.vehicle.id, footprint: e, latency: latency, tracked: e.vehicle.id === selectedVehicleId, start: start, stop: stop, onClick: () => setSelectedVehicleId?.(e.vehicle.id) })))
                : null,
            showRadar && radarRadius > 0 && radarLocation ? (React.createElement(RadarEntity, { radar: { location: radarLocation, radius: radarRadius } })) : null)));
}
export default React.memo(MonitorPanel);
